// Cross-channel customer journey: path chips + chronological touch timeline.
import { useEffect, useState } from "react";
import {
  MessageCircle, Mail, Phone, PhoneMissed, Instagram, Send, Facebook, Voicemail,
  MessageSquare, Globe, Loader2, Check, CheckCheck, AlertCircle, Bot, User, Headset, Flag,
} from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { fmtDateTime } from "@/lib/app-utils";

const COLORS = {
  whatsapp: "#25D366", email: "#0ea5e9", sms: "#f59e0b", telegram: "#229ED9",
  instagram: "#dc2743", facebook: "#1877F2", viber: "#7360F2", webchat: "#14b8a6",
  voicemail: "#a855f7", voice: "#6366f1", whatsapp_call: "#16a34a",
};
const ICONS = {
  whatsapp: MessageCircle, email: Mail, sms: MessageSquare, telegram: Send, instagram: Instagram,
  facebook: Facebook, viber: MessageCircle, webchat: Globe, voicemail: Voicemail, voice: Phone, whatsapp_call: Phone,
};

function StatusIcon({ s }) {
  if (s === "read") return <CheckCheck className="w-3.5 h-3.5 text-sky-500" />;
  if (s === "delivered") return <CheckCheck className="w-3.5 h-3.5 text-muted-foreground" />;
  if (s === "failed") return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  return <Check className="w-3.5 h-3.5 text-muted-foreground" />;
}

function ActorIcon({ a }) {
  if (a === "bot") return <Bot className="w-3 h-3" />;
  if (a === "agent") return <Headset className="w-3 h-3" />;
  return <User className="w-3 h-3" />;
}

function Row({ it }) {
  const missed = it.event === "call_missed";
  const Icon = missed ? PhoneMissed : it.event === "conversation_started" ? Flag : (ICONS[it.channel] || MessageCircle);
  const color = COLORS[it.channel] || "#64748b";
  const isCall = it.event.startsWith("call_");
  return (
    <li className="relative pl-9 pb-3" data-testid={`journey-item-${it.id}`}>
      <span className="absolute left-0 top-0 w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ background: color }}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
        <span className="font-semibold" style={{ color }}>{it.channel_label}</span>
        <span className="text-foreground/80">
          {isCall ? `${it.direction === "inbound" ? "Inbound" : "Outbound"} · ${it.event_label}` : it.event === "received" ? "Customer message" : it.event === "sent" ? "Reply sent" : it.event_label}
        </span>
        <span className="inline-flex items-center gap-1 text-muted-foreground"><ActorIcon a={it.actor} />{it.agent_name || it.actor}</span>
        {it.event === "sent" && <StatusIcon s={it.status} />}
        {isCall && it.duration_seconds > 0 && <span className="font-mono text-muted-foreground">{Math.round(it.duration_seconds)}s</span>}
        <span className="ml-auto text-muted-foreground font-mono text-[10px]">{fmtDateTime(it.at)}</span>
      </div>
      {it.preview && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{it.preview}</div>}
    </li>
  );
}

export default function JourneyTimeline({ contactId, identity, compact = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!contactId && !identity) return;
    setData(null); setErr("");
    const params = contactId ? { contact_id: contactId } : { identity };
    api.get("/omni-reports/journey/timeline", { params })
      .then((r) => setData(r.data)).catch((e) => setErr(formatErr(e)));
  }, [contactId, identity]);

  if (err) return <div className="text-xs text-red-500" data-testid="journey-error">{err}</div>;
  if (!data) return <div className="py-6 text-center text-muted-foreground" data-testid="journey-loading"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  const s = data.summary || {};
  const items = compact ? data.timeline.slice(0, 40) : data.timeline;
  return (
    <div data-testid="journey-timeline">
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="journey-path">
        {(s.channels || []).map((c, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-[11px]">
            {i > 0 && <span className="text-muted-foreground">→</span>}
            <span className="px-2 py-0.5 rounded-full bg-muted font-medium">{c}</span>
          </span>
        ))}
        <span className="text-[11px] text-muted-foreground ml-auto" data-testid="journey-summary">
          {s.touches || 0} touches · {s.channel_count || 0} channel(s)
        </span>
      </div>
      {items.length === 0
        ? <div className="text-xs text-muted-foreground italic py-4" data-testid="journey-empty">No interactions recorded yet.</div>
        : <ol className="border-l border-border ml-3.5 -translate-x-3.5">{items.map((it) => <Row key={it.id} it={it} />)}</ol>}
    </div>
  );
}
